/**
 * Subscription Controller
 *
 * Handles consumer subscription operations including:
 * - Fetching available plans
 * - Getting subscription status
 * - Creating checkout sessions
 * - Managing subscription (cancel/reactivate)
 * - Customer portal access
 *
 * SECURITY: Checkout sessions are created server-side.
 * The client only receives a Stripe session URL to redirect to.
 */

import { ApiInstance } from "../instance";

// Subscription plan from backend
export interface SubscriptionPlan {
  id: 'premium_weekly' | 'premium_monthly' | 'premium_annual' | 'family_monthly' | 'family_annual';
  name: string;
  price: number;
  interval: 'week' | 'month' | 'year';
  monthlyEquivalent?: number;
  savings?: string;
  features: string[];
  trialDays: number;
}

// Subscription status response
export interface SubscriptionStatus {
  isSubscribed: boolean;
  isPremium: boolean;
  isPastDue?: boolean;
  monthlyFallbackOffered?: boolean;
  plan: string | null;
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'paused' | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
}

// Checkout session response (redirect mode)
export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// Embedded checkout session response
export interface EmbeddedCheckoutResponse {
  sessionId: string;
  clientSecret: string;
}

// Checkout session status (for verifying completion)
export interface CheckoutStatusResponse {
  status: string;
  paymentStatus: string;
  customerEmail: string | null;
  subscriptionCreated: boolean;
}

// Portal session response
export interface PortalSessionResponse {
  url: string;
}

// Invoice from billing history
export interface Invoice {
  id: string;
  amount_paid: number;
  status: string;
  created: string;
  invoice_pdf: string | null;
  description: string;
}

export const SubscriptionController = {
  /**
   * Get available subscription plans (public endpoint)
   */
  getPlans: async () => {
    return await ApiInstance.get<{ plans: SubscriptionPlan[] }>('/consumers/subscription/plans');
  },

  /**
   * Get current subscription status for the authenticated user
   */
  getStatus: async () => {
    return await ApiInstance.get<SubscriptionStatus>('/consumers/subscription/status');
  },

  /**
   * Check if user has premium access
   */
  checkAccess: async () => {
    return await ApiInstance.get<{ hasPremium: boolean }>('/consumers/subscription/check-access');
  },

  /**
   * Create a Stripe checkout session for subscription (redirect mode — legacy)
   * Returns a URL to redirect the user to Stripe Checkout
   */
  createCheckout: async (payload: {
    plan: SubscriptionPlan['id'];
    successUrl?: string;
    cancelUrl?: string;
  }) => {
    return await ApiInstance.post<CheckoutSessionResponse>('/consumers/subscription/checkout', payload);
  },

  /**
   * Create an embedded Stripe checkout session (in-app, no redirect)
   * Returns a clientSecret for use with <EmbeddedCheckout>
   */
  createEmbeddedCheckout: async (payload: {
    plan: SubscriptionPlan['id'];
    returnUrl?: string;
    /** Meta browser/click cookies — stashed on the Stripe session for the
     *  server-side Conversions API Purchase. Optional, null when absent. */
    fbp?: string | null;
    fbc?: string | null;
  }) => {
    return await ApiInstance.post<EmbeddedCheckoutResponse>('/consumers/subscription/checkout/embedded', payload);
  },

  /**
   * Get checkout session status (for verifying completion after embedded checkout)
   */
  getCheckoutStatus: async (sessionId: string) => {
    return await ApiInstance.get<CheckoutStatusResponse>(`/consumers/subscription/checkout/status?session_id=${sessionId}`);
  },

  /**
   * Create a Stripe customer portal session
   * Returns a URL to redirect the user to manage their subscription
   */
  createPortal: async (payload?: { returnUrl?: string }) => {
    return await ApiInstance.post<PortalSessionResponse>('/consumers/subscription/portal', payload || {});
  },

  /**
   * Cancel subscription at period end
   */
  cancel: async () => {
    return await ApiInstance.post<{ success: boolean; accessUntil: string }>('/consumers/subscription/cancel');
  },

  /**
   * Reactivate a subscription scheduled for cancellation
   */
  reactivate: async () => {
    return await ApiInstance.post<{ success: boolean }>('/consumers/subscription/reactivate');
  },

  /**
   * Get billing history (past invoices)
   */
  getInvoices: async () => {
    return await ApiInstance.get<{ invoices: Invoice[] }>('/consumers/subscription/invoices');
  },

  /**
   * Accept the monthly fallback offer (past_due annual users only)
   * Switches from annual to monthly plan with explicit user consent
   */
  acceptMonthlyFallback: async () => {
    return await ApiInstance.post<{ success: boolean; plan: string }>('/consumers/subscription/accept-monthly-fallback');
  },
};
